// Guest page data. Words: GUEST_PAGE_WORDS.md (approved 2026-10-06).
// The guest page ports as designed. This file turns a Firebase asset into the
// rows the live page shows, one row at a time, so the page itself only draws.
// Shared by the server step (functions/src/guest.ts) and the unit tests.
// Nothing here reads or writes the database.

import { siteFromAssetId } from "./company";

/** Shown for any row the record leaves blank. Same as the live page. */
export const BLANK = "—";

/** What the live page says in the Incident Energy cell for every real asset today (risk decision 39). */
export const IE_PENDING = "IE pending study";

/**
 * Demo labels hang at every site with the same suffix. The site number in front changes.
 * 01-ER1-MCC-25-C4 opens the mcc template with Springfield's name.
 * Keys match the demoAssets documents written by the registry copy.
 */
export const DEMO_SUFFIXES: Record<string, DemoKey> = {
  "-ER1-MCC-25-C4": "mcc",
  "-EA1-MSD-25": "msd",
  "-LC1-PNL-25": "panel",
};
export type DemoKey = "mcc" | "msd" | "panel";

/** The demo template a label opens, or null for a real asset. */
export function demoKeyFor(assetId: string): DemoKey | null {
  const site = siteFromAssetId(assetId);
  if (!site) return null;
  return DEMO_SUFFIXES[assetId.slice(2)] ?? null;
}

// ---- What Firestore holds ----

/** Site fields the guest page reads. sites/{siteNumber}. */
export interface SiteDoc {
  siteNumber?: string;
  name?: string;
  city?: string;
  state?: string;
  isActive?: boolean;
}

/** Asset fields the guest page reads. sites/{site}/assets/{assetId} or demoAssets/{key}. */
export interface AssetDoc {
  assetId?: string;
  location?: string;
  equipmentType?: string;
  circuitDescription?: string;
  nominalVoltage?: string;
  nominalVolts?: number | null;
  hasShockHazard?: boolean;
  limitedApproach?: string;
  isProperlyInstalled?: boolean;
  isUsedPerInstructions?: boolean;
  ieStatus?: string;
  incidentEnergyCal?: number;
  arcFlashBoundary?: string;
  isOverduty?: boolean;
  /** "KE · 06/10/2026", written by Publish on the Arc Flash Labels page. */
  afStudy?: string;
  doorsClosedSecured?: boolean;
  coversInPlace?: boolean;
  properlyMaintained?: boolean;
  impendingFailurePresent?: boolean;
  conditionOfMaintenance?: string;
  lastAssessedOn?: string;
  isRemoved?: boolean;
}

// ---- What the page draws ----

/** One NOC dot. true = satisfactory, false = not, null = not yet assessed (grey "?"). */
export type NocState = boolean | null;

export interface GuestView {
  assetId: string;
  siteName: string;
  isDemo: boolean;
  location: string;
  equipmentType: string;
  deviceType: string;
  description: string;
  voltage: string;
  limitedApproach: string;
  /** "YES", "NO" or null for the grey dash. */
  shockHazard: "YES" | "NO" | null;
  incidentEnergy: string;
  arcFlashBoundary: string;
  arcFlashHazard: "YES" | "NO" | null;
  afStudy: string;
  nextAssessment: string;
  /** Seven dots in the live order: Installed, Maintained, AIC, Approved Use, Covers, Enclosure, Impending Failure. */
  noc: NocState[];
  lastAssessed: string;
  lastPm: string;
  lastAudit: string;
}

export type GuestResult =
  | { kind: "asset"; view: GuestView }
  | { kind: "notActive"; assetId: string; siteName: string }
  | { kind: "notFound"; assetId: string };

/** "Springfield, KY", the way the live page names a site. */
export function siteDisplayName(site: SiteDoc | null | undefined): string {
  if (!site) return "";
  const place = (site.city || site.name || "").trim();
  const state = (site.state || "").trim();
  return place && state ? `${place}, ${state}` : place;
}

/** 2026-08-27 -> 08/27/2026. Anything else is shown as stored. */
export function usDate(iso: string | undefined): string {
  const s = (iso ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s || BLANK;
}

function text(v: string | undefined): string {
  const s = (v ?? "").trim();
  return s || BLANK;
}

/** Voltage as the live page prints it: "480V (P-P)" stays, a bare 480 gets a V. */
export function voltageText(v: string | undefined): string {
  const s = (v ?? "").trim();
  if (!s) return BLANK;
  return /[Vv()]/.test(s) ? s : `${s}V`;
}

/** Shock Hazard pill. The record's own answer wins; the voltage (50 V or more) covers a blank. */
export function shockHazardOf(a: AssetDoc): "YES" | "NO" | null {
  if (a.hasShockHazard === true) return "YES";
  if (a.hasShockHazard === false) return "NO";
  const volts = typeof a.nominalVolts === "number" ? a.nominalVolts : parseFloat(a.nominalVoltage ?? "");
  if (!Number.isFinite(volts) || volts === 0) return null;
  return volts >= 50 ? "YES" : "NO";
}

/** Incident Energy cell. A study number when there is one, else the status words. */
export function incidentEnergyText(a: AssetDoc): string {
  if (typeof a.incidentEnergyCal === "number") return `${a.incidentEnergyCal} cal/cm²`;
  return text(a.ieStatus);
}

/** Arc Flash Hazard pill. YES at 1.2 cal/cm² or more. A dash while the study is pending. */
export function arcFlashHazardOf(a: AssetDoc): "YES" | "NO" | null {
  if (typeof a.incidentEnergyCal !== "number") return null;
  return a.incidentEnergyCal >= 1.2 ? "YES" : "NO";
}

function yes(v: boolean | undefined): NocState {
  return v === true ? true : v === false ? false : null;
}

/**
 * The seven NOC dots, in the live order and with the live wiring (S120):
 * Covers Secured reads covers in place (Q5), Enclosure Intact reads doors closed (Q4),
 * Within AIC Rating fails on overduty, Impending Failure passes when none is present.
 */
export function nocStatesOf(a: AssetDoc): NocState[] {
  return [
    yes(a.isProperlyInstalled),
    yes(a.properlyMaintained),
    a.isOverduty === true ? false : a.isOverduty === false ? true : null,
    yes(a.isUsedPerInstructions),
    yes(a.coversInPlace),
    yes(a.doorsClosedSecured),
    a.impendingFailurePresent === true ? false : a.impendingFailurePresent === false ? true : null,
  ];
}

/** Builds the page rows. The asset ID is the one scanned; a demo label keeps its own ID. */
export function guestViewOf(assetId: string, asset: AssetDoc, site: SiteDoc | null, isDemo: boolean): GuestView {
  return {
    assetId,
    siteName: siteDisplayName(site),
    isDemo,
    location: text(asset.location),
    equipmentType: text(asset.equipmentType),
    deviceType: BLANK,
    description: text(asset.circuitDescription),
    voltage: voltageText(asset.nominalVoltage),
    limitedApproach: text(asset.limitedApproach),
    shockHazard: shockHazardOf(asset),
    incidentEnergy: incidentEnergyText(asset),
    arcFlashBoundary: text(asset.arcFlashBoundary),
    arcFlashHazard: arcFlashHazardOf(asset),
    afStudy: text(asset.afStudy),
    nextAssessment: BLANK,
    noc: nocStatesOf(asset),
    lastAssessed: usDate(asset.lastAssessedOn),
    lastPm: BLANK,
    lastAudit: BLANK,
  };
}

/**
 * The whole decision for one scan, given what the database returned.
 * Demo labels open at any site that exists, active or not (approved 2026-10-06).
 * A real asset at a site that is not active shows the site-not-active notice.
 */
export function guestResultOf(input: {
  assetId: string;
  site: SiteDoc | null;
  asset: AssetDoc | null;
  demo: AssetDoc | null;
}): GuestResult {
  const { assetId, site, asset, demo } = input;
  if (!site) return { kind: "notFound", assetId };
  const isDemo = demoKeyFor(assetId) !== null;
  if (isDemo) {
    return demo ? { kind: "asset", view: guestViewOf(assetId, demo, site, true) } : { kind: "notFound", assetId };
  }
  if (site.isActive !== true) return { kind: "notActive", assetId, siteName: siteDisplayName(site) };
  if (!asset || asset.isRemoved === true) return { kind: "notFound", assetId };
  return { kind: "asset", view: guestViewOf(assetId, asset, site, false) };
}

/** The Asset ID explainer segments, same split as the live gBuildIdSegments. */
export function idSegments(assetId: string): { value: string; label: string }[] {
  const parts = assetId.split("-");
  if (parts.length < 3) return [];
  const site = parts[0]!;
  const area = parts[1]!;
  const device = parts[parts.length - 1]!;
  const equip = parts.slice(2, parts.length - 1).join("-") || device;
  const up = area.toUpperCase();
  const areaLabel = up.startsWith("ER") ? "El. Room" : up.startsWith("EA") ? "El. Area" : "Area";
  return parts.length === 3
    ? [
        { value: site, label: "Site" },
        { value: area, label: areaLabel },
        { value: device, label: "Device" },
      ]
    : [
        { value: site, label: "Site" },
        { value: area, label: areaLabel },
        { value: equip, label: "Equipment" },
        { value: device, label: "Device" },
      ];
}
